import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Upload, FileText, Check, X, AlertCircle, CreditCard, ImageIcon, Loader2, Eye, Download, MousePointerClick } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function VendorDashboard() {
  const { user, authHeaders, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [vendor, setVendor] = useState(null);
  const [categories, setCategories] = useState([]);
  const [pricing, setPricing] = useState({ monthly_price: 99, featured_price: 149, featured_annual_price: 1788 });
  const [analytics, setAnalytics] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ logo: false, line_card: false });

  const [form, setForm] = useState({
    company_name: "", contact_name: "", phone: "", website: "",
    description: "", city: "", state: "",
    categories: [], keywords: [],
    logo_path: null, line_card_path: null, line_card_filename: null,
  });
  const [keywordInput, setKeywordInput] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login", { state: { from: "/dashboard" } }); return; }
    Promise.all([
      axios.get(`${API}/vendor/me`, { headers: authHeaders() }),
      axios.get(`${API}/categories`),
      axios.get(`${API}/config/pricing`),
      axios.get(`${API}/vendor/analytics`, { headers: authHeaders() }),
    ]).then(([v, c, p, a]) => {
      setCategories(c.data);
      setPricing(p.data);
      setAnalytics(a.data);
      if (v.data) {
        setVendor(v.data);
        setForm({
          company_name: v.data.company_name || "",
          contact_name: v.data.contact_name || "",
          phone: v.data.phone || "",
          website: v.data.website || "",
          description: v.data.description || "",
          city: v.data.city || "",
          state: v.data.state || "",
          categories: v.data.categories || [],
          keywords: v.data.keywords || [],
          logo_path: v.data.logo_path || null,
          line_card_path: v.data.line_card_path || null,
          line_card_filename: v.data.line_card_filename || null,
        });
      }
    }).catch(() => {});
  }, [user, authLoading, navigate]); // eslint-disable-line

  const handleUpload = async (field, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [field]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/uploads`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      if (field === "logo") {
        setForm((f) => ({ ...f, logo_path: data.path }));
      } else {
        setForm((f) => ({ ...f, line_card_path: data.path, line_card_filename: data.filename }));
      }
      toast.success(`${field === "logo" ? "Logo" : "Line card"} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [field]: false }));
    }
  };

  const toggleCat = (name) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(name) ? f.categories.filter((c) => c !== name) : [...f.categories, name],
    }));
  };

  const addKeyword = () => {
    const k = keywordInput.trim();
    if (!k || form.keywords.includes(k)) { setKeywordInput(""); return; }
    setForm((f) => ({ ...f, keywords: [...f.keywords, k] }));
    setKeywordInput("");
  };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/vendor/profile`, form, { headers: authHeaders() });
      setVendor(data);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const subscribe = async (tier = "basic") => {
    if (!vendor) {
      toast.error("Save your profile first");
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/payments/checkout`,
        { origin_url: window.location.origin, tier },
        { headers: authHeaders() }
      );
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    }
  };

  if (authLoading || !user) return null;

  const statusBadge = () => {
    if (!vendor) return { label: "Profile incomplete", color: "bg-slate-700 text-slate-200", icon: AlertCircle };
    if (vendor.subscription_active && vendor.approved) return { label: "Live", color: "bg-emerald-600 text-white", icon: Check };
    if (vendor.subscription_active && !vendor.approved) return { label: "Awaiting Approval", color: "bg-amber-600 text-white", icon: AlertCircle };
    return { label: "Not Subscribed", color: "bg-slate-700 text-slate-200", icon: CreditCard };
  };
  const status = statusBadge();
  const StatusIcon = status.icon;

  return (
    <div className="px-6 sm:px-12 lg:px-24 py-12 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-3">// Vendor Dashboard</div>
          <h1 className="font-display font-black uppercase tracking-tighter text-4xl text-slate-50">Your Profile</h1>
        </div>
        <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-sm ${status.color}`} data-testid="vendor-status">
          <StatusIcon className="w-4 h-4" />
          <span className="text-xs font-bold uppercase tracking-wider">{status.label}</span>
        </div>
      </div>

      {params.get("from") === "register" && (
        <div className="mb-8 bg-orange-600/10 border border-orange-600/40 p-4 rounded-sm">
          <p className="text-sm text-orange-100">Complete your profile and subscribe to be listed in the directory.</p>
        </div>
      )}

      {vendor && analytics && (
        <div className="mb-10">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-4">// Analytics · Last 30 Days</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <AnalyticsCard icon={Eye} label="Profile Views" value={analytics.profile_view} testid="analytics-views" />
            <AnalyticsCard icon={Download} label="Line Card Downloads" value={analytics.linecard_download} testid="analytics-downloads" />
            <AnalyticsCard icon={MousePointerClick} label="Contact Clicks" value={analytics.contact_click} testid="analytics-clicks" />
          </div>
          {vendor.subscription_tier !== "featured" && analytics.profile_view > 0 && (
            <div className="mt-4 bg-amber-500/10 border border-amber-500/40 p-4 rounded-sm flex items-center justify-between">
              <p className="text-sm text-amber-100">Featured vendors get up to <span className="font-bold">5× more profile views</span>. Upgrade now.</p>
              <button
                data-testid="analytics-upgrade-cta"
                onClick={() => subscribe("featured")}
                className="text-xs font-bold uppercase tracking-wider px-4 py-2 bg-amber-500 text-slate-950 hover:bg-amber-400 rounded-sm"
              >
                Upgrade
              </button>
            </div>
          )}
        </div>
      )}

      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main fields */}
        <div className="lg:col-span-2 space-y-6">
          <Section title="Company Information">
            <Field label="Company Name *" testid="vp-company">
              <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Contact Name *" testid="vp-contact">
              <input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inputCls} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Phone *" testid="vp-phone">
                <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Website" testid="vp-website">
                <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." className={inputCls} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="City" testid="vp-city">
                <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
              </Field>
              <Field label="State" testid="vp-state">
                <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <Field label="Description" testid="vp-description">
              <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls + " resize-none"} placeholder="Tell buyers about your services, certifications, and what makes you the right vendor..." />
            </Field>
          </Section>

          <Section title="Categories">
            <p className="text-sm text-slate-400 mb-4">Select trades you serve.</p>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  data-testid={`vp-cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => toggleCat(c.name)}
                  className={`text-xs font-bold uppercase tracking-wider px-3 py-2 border transition-colors ${
                    form.categories.includes(c.name)
                      ? "bg-orange-600 text-white border-orange-600"
                      : "bg-slate-800 text-slate-300 border-slate-700 hover:border-orange-600"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Keywords">
            <p className="text-sm text-slate-400 mb-4">Add specific services, capabilities, and search terms.</p>
            <div className="flex gap-2 mb-3">
              <input
                value={keywordInput}
                data-testid="vp-keyword-input"
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
                placeholder="e.g., stainless steel welding"
                className={inputCls + " flex-1"}
              />
              <button type="button" data-testid="vp-keyword-add" onClick={addKeyword} className="px-5 bg-slate-800 border border-slate-700 text-slate-200 hover:border-orange-600 font-bold uppercase tracking-wider text-xs rounded-sm">
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.keywords.map((k) => (
                <span key={k} className="inline-flex items-center gap-1.5 text-xs font-mono px-2 py-1 bg-slate-800 text-slate-300 border border-slate-700">
                  #{k}
                  <button type="button" onClick={() => setForm({ ...form, keywords: form.keywords.filter((x) => x !== k) })} className="text-slate-500 hover:text-orange-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </Section>
        </div>

        {/* Sidebar - uploads + subscription */}
        <aside className="space-y-6">
          <Section title="Logo">
            <UploadBox
              field="logo"
              accept="image/*"
              uploading={uploading.logo}
              onUpload={handleUpload}
              preview={form.logo_path ? (
                <img src={`${API_BASE}/api/files/${form.logo_path}`} alt="Logo" className="w-full h-32 object-contain bg-slate-950 border border-slate-800" />
              ) : null}
              icon={ImageIcon}
              hint="PNG / JPG up to 10MB"
            />
            {form.logo_path && (
              <button type="button" onClick={() => setForm({ ...form, logo_path: null })} className="mt-2 text-xs text-slate-500 hover:text-orange-500 uppercase tracking-wider">Remove logo</button>
            )}
          </Section>

          <Section title="Line Card (PDF)">
            <UploadBox
              field="line_card"
              accept="application/pdf"
              uploading={uploading.line_card}
              onUpload={handleUpload}
              preview={form.line_card_path ? (
                <div className="flex items-center gap-3 p-3 bg-slate-950 border border-slate-800 rounded-sm">
                  <FileText className="w-5 h-5 text-orange-500" />
                  <span className="text-xs text-slate-300 truncate flex-1">{form.line_card_filename || "line-card.pdf"}</span>
                </div>
              ) : null}
              icon={FileText}
              hint="PDF up to 10MB"
            />
            {form.line_card_path && (
              <button type="button" onClick={() => setForm({ ...form, line_card_path: null, line_card_filename: null })} className="mt-2 text-xs text-slate-500 hover:text-orange-500 uppercase tracking-wider">Remove file</button>
            )}
          </Section>

          <button
            type="submit"
            data-testid="vp-save"
            disabled={saving}
            className="w-full py-3 bg-orange-600 text-white font-bold uppercase tracking-wider hover:bg-orange-500 disabled:opacity-50 rounded-sm"
          >
            {saving ? "Saving..." : (vendor ? "Update Profile" : "Save Profile")}
          </button>

          {/* Subscription card */}
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-sm">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-3">Subscription</div>
            {vendor?.subscription_active ? (
              <div>
                <div className="flex items-center gap-2 text-emerald-500 mb-2">
                  <Check className="w-4 h-4" />
                  <span className="text-sm font-bold uppercase tracking-wider">
                    Active · {vendor.subscription_tier === "featured_annual" ? "Featured (Annual)" : vendor.subscription_tier === "featured" ? "Featured" : "Basic"}
                  </span>
                </div>
                {vendor.subscription_expires_at && (
                  <p className="text-xs text-slate-500 font-mono mb-4">
                    Renews / expires: {new Date(vendor.subscription_expires_at).toLocaleDateString()}
                  </p>
                )}
                {vendor.subscription_tier === "basic" && (
                  <div className="space-y-2">
                    <button
                      type="button"
                      data-testid="vp-upgrade-featured"
                      onClick={() => subscribe("featured")}
                      className="w-full py-3 border border-amber-500 text-amber-400 font-bold uppercase tracking-wider hover:bg-amber-500/10 rounded-sm flex items-center justify-center gap-2 text-xs"
                    >
                      Upgrade to Featured · ${pricing.featured_price}/mo
                    </button>
                    <button
                      type="button"
                      data-testid="vp-upgrade-featured-annual"
                      onClick={() => subscribe("featured_annual")}
                      className="w-full py-3 bg-amber-400 text-slate-950 font-bold uppercase tracking-wider hover:bg-amber-300 rounded-sm flex items-center justify-center gap-2 text-xs"
                    >
                      Upgrade Annual · ${pricing.featured_annual_price?.toLocaleString?.() || pricing.featured_annual_price}/yr
                    </button>
                  </div>
                )}
                {vendor.subscription_tier === "featured" && (
                  <button
                    type="button"
                    data-testid="vp-switch-featured-annual"
                    onClick={() => subscribe("featured_annual")}
                    className="w-full py-3 bg-amber-400 text-slate-950 font-bold uppercase tracking-wider hover:bg-amber-300 rounded-sm flex items-center justify-center gap-2 text-xs"
                  >
                    Switch to Annual · ${pricing.featured_annual_price?.toLocaleString?.() || pricing.featured_annual_price}/yr
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-3 mb-3">
                  <div className="border border-slate-700 p-3 rounded-sm">
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs uppercase tracking-wider text-slate-400 font-bold">Alliance Member</span>
                      <span className="font-display font-black text-xl text-slate-50">${pricing.monthly_price}<span className="text-xs text-slate-500">/mo</span></span>
                    </div>
                    <button
                      type="button"
                      data-testid="vp-subscribe-basic"
                      onClick={() => subscribe("basic")}
                      className="w-full mt-3 py-2 border border-orange-600 text-orange-500 font-bold uppercase tracking-wider hover:bg-orange-600/10 rounded-sm flex items-center justify-center gap-2 text-xs"
                    >
                      <CreditCard className="w-3 h-3" /> Subscribe
                    </button>
                  </div>
                  <div className="border border-amber-500/50 p-3 rounded-sm bg-amber-500/5">
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs uppercase tracking-wider text-amber-400 font-bold">Featured Monthly</span>
                      <span className="font-display font-black text-xl text-slate-50">${pricing.featured_price}<span className="text-xs text-slate-500">/mo</span></span>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">Homepage placement + priority search</p>
                    <button
                      type="button"
                      data-testid="vp-subscribe-featured"
                      onClick={() => subscribe("featured")}
                      className="w-full mt-3 py-2 bg-amber-500 text-slate-950 font-bold uppercase tracking-wider hover:bg-amber-400 rounded-sm flex items-center justify-center gap-2 text-xs"
                    >
                      <CreditCard className="w-3 h-3" /> Subscribe
                    </button>
                  </div>
                  <div className="border border-amber-400/70 p-3 rounded-sm bg-amber-400/10">
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs uppercase tracking-wider text-amber-300 font-bold">Featured Annual</span>
                      <span className="font-display font-black text-xl text-slate-50">${pricing.featured_annual_price?.toLocaleString?.() || pricing.featured_annual_price}<span className="text-xs text-slate-500">/yr</span></span>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">Same features · one yearly payment</p>
                    <button
                      type="button"
                      data-testid="vp-subscribe-featured-annual"
                      onClick={() => subscribe("featured_annual")}
                      className="w-full mt-3 py-2 bg-amber-400 text-slate-950 font-bold uppercase tracking-wider hover:bg-amber-300 rounded-sm flex items-center justify-center gap-2 text-xs"
                    >
                      <CreditCard className="w-3 h-3" /> Subscribe Yearly
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </aside>
      </form>
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 bg-slate-950 border border-slate-700 text-slate-50 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none rounded-sm";

function AnalyticsCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-slate-900 border border-slate-800 p-5 rounded-sm">
      <Icon className="w-5 h-5 text-orange-500 mb-3" />
      <div className="font-display font-black text-3xl text-slate-50">{value}</div>
      <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-slate-900 border border-slate-800 p-6 rounded-sm space-y-4">
      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 block">{label}</label>
      {children}
    </div>
  );
}

function UploadBox({ field, accept, uploading, onUpload, preview, icon: Icon, hint }) {
  const ref = useRef();
  return (
    <div>
      {preview}
      <button
        type="button"
        data-testid={`vp-upload-${field.replace("_", "-")}`}
        onClick={() => ref.current?.click()}
        disabled={uploading}
        className="mt-3 w-full border-2 border-dashed border-slate-700 hover:border-orange-500 transition-colors p-6 rounded-sm flex flex-col items-center text-center disabled:opacity-50"
      >
        {uploading ? (
          <Loader2 className="w-6 h-6 text-orange-500 animate-spin mb-2" />
        ) : (
          <Upload className="w-6 h-6 text-slate-500 mb-2" />
        )}
        <span className="text-sm font-bold uppercase tracking-wider text-slate-300">
          {(() => {
            if (uploading) return "Uploading...";
            if (preview) return "Replace";
            return "Upload File";
          })()}
        </span>
        <span className="text-xs text-slate-500 mt-1">{hint}</span>
      </button>
      <input
        type="file"
        accept={accept}
        ref={ref}
        className="hidden"
        onChange={(e) => onUpload(field, e.target.files?.[0])}
      />
    </div>
  );
}
